import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setLocationHeader, flushOutbox, errKind, errRef } from "@/lib/api";

const AuthCtx = createContext(null);
const REFRESH_MS = 5 * 60 * 1000;
const RESUME_MS = 30 * 1000;

const complete = (d) => !!d && (d.user?.role === "superadmin" || !d.org || (Array.isArray(d.modules) && Array.isArray(d.role_modules)));

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [locationId, setLocationId] = useState(localStorage.getItem("86d_loc"));
  const qc = useQueryClient();
  const seq = useRef(0);
  const last = useRef(0);
  const has = useRef(false);

  const apply = useCallback((data) => {
    if (!complete(data)) return;
    seq.current += 1;
    last.current = Date.now();
    const locs = data.locations || [];
    const saved = localStorage.getItem("86d_loc");
    const loc = locs.find((l) => l.id === saved) ? saved : locs[0]?.id || null;
    setLocationHeader(loc);
    setLocationId(loc);
    has.current = !!data.user;
    setSession(data);
    setAuthError(null);
    setRefreshError(null);
    setExpired(false);
  }, []);

  // Only the newest /auth/me response may apply; a failed refresh never replaces an established session with empty access.
  const reload = useCallback(async () => {
    const mine = ++seq.current;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { data } = await api.get("/auth/me", { timeout: 15000 });
        if (mine !== seq.current) return data;
        if (!complete(data)) throw Object.assign(new Error("incomplete session"), { incomplete: true });
        apply(data);
        return data;
      } catch (e) {
        if (mine !== seq.current) return false;
        const kind = e.incomplete ? "unavailable" : errKind(e);
        if (kind === "expired" || (kind !== "unavailable" && !has.current)) { has.current = false; setSession(false); return false; }
        if (attempt === 1 || kind !== "unavailable") {
          if (has.current) setRefreshError({ ref: errRef(e) }); else setAuthError({ ref: errRef(e) });
          return false;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    return false;
  }, [apply]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    reload();
  }, [reload]);

  useEffect(() => {
    const onExpired = () => { has.current = false; setExpired(true); setSession(false); };
    const resume = () => { if (document.visibilityState === "visible" && has.current && Date.now() - last.current > RESUME_MS) reload(); };
    const onStorage = (e) => { if (e.key === "86d_session_ping" && has.current) reload(); };
    const timer = setInterval(() => { if (has.current && document.visibilityState === "visible") reload(); }, REFRESH_MS);
    window.addEventListener("session-expired", onExpired);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("storage", onStorage);
    window.addEventListener("access-changed", reload);
    return () => {
      clearInterval(timer);
      window.removeEventListener("session-expired", onExpired);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("access-changed", reload);
    };
  }, [reload]);

  const switchLocation = (id) => {
    setLocationHeader(id);
    setLocationId(id);
    qc.invalidateQueries();
  };

  const logout = async () => {
    seq.current += 1;
    await flushOutbox().catch(() => {});
    await api.post("/auth/logout").catch(() => {});
    Object.keys(localStorage).filter((k) => k.startsWith("86d_")).forEach((k) => localStorage.removeItem(k));
    localStorage.setItem("86d_session_ping", String(Date.now()));
    sessionStorage.clear();
    qc.clear();
    has.current = false;
    setExpired(false);
    setSession(false);
  };

  const s = session || {};
  const value = {
    session, authError, refreshError, expired, apply, reload, logout, switchLocation, locationId,
    user: s.user, org: s.org, locations: s.locations || [], modules: s.modules || [], features: s.features || [], state: s.state, financial: !!s.financial,
    featurePlans: s.feature_plans || {}, moduleFeatures: s.module_features || {},
    location: (s.locations || []).find((l) => l.id === locationId),
    can: (m) => (s.modules || []).includes(m),
    allowed: (m) => (s.role_modules || s.modules || []).includes(m),
    planFor: (m) => (s.feature_plans || {})[(s.module_features || {})[m] || "ops.core"] || "ops_pro",
    has: (f, a = "view") => s.user?.role === "owner" || (((s.perms || {})[f]) || []).includes(a),
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function useGet(path, opts = {}) {
  const { locationId } = useAuth();
  return useQuery({
    queryKey: [path, locationId],
    queryFn: () => api.get(path).then((r) => r.data),
    enabled: !!path && (opts.enabled ?? true),
    ...opts,
  });
}

export function useSettings() {
  const q = useGet("/org", { staleTime: 300000 });
  return { settings: q.data?.location?.settings || {}, location: q.data?.location, org: q.data?.org, refetch: q.refetch };
}
